import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import { ButtonUI, CustomThemeProvider, ModalComponent, useQueryFetch } from 'sonata-react-components';
import { checkUserResourcePermissions, checkUserRouteAccess } from 'sonata-static-storage/lib/helpers/user.helpers';
import { News } from 'sonata-static-storage/lib/schemas/news/news.schema';
import { NewsViews } from 'sonata-static-storage/lib/schemas/news/newsViews.schema';

import { useClickOutside } from '../../../hooks/useClickOutside';
import { getNews, getNewsViewsById, postNewsViews, updateNewsViews } from '../../../services/news.service';
import { useMenu } from '../Menu.context';
import { NewsFeatures } from './NewsFeatures/NewsFeatures';

import './newsButtonMenu.scss';

import type { ThemeType } from 'sonata-react-components';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

const VALID_ROUTES = ['/campaigns'];

type Props = {
  theme?: ThemeType;
  user: UserInterface<string>;
  userRoles: UserRoleInterface[];
};

type State = {
  isActive: boolean;
  showModal: boolean;
};

export function NewsButtonMenu({ theme = 'light', user, userRoles }: Props) {
  const { formatMessage } = useIntl();
  const { showNotificationSettingsModal } = useMenu();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const ref = useRef<HTMLDivElement>(null);

  const [{ isActive, showModal }, setState] = useState<State>({
    isActive: false,
    showModal: false
  });
  useClickOutside(ref, () => setState(prev => ({ ...prev, isActive: false })));

  const tokens: Record<string, Record<string, any>> = {
    newsToken: {},
    newsViewsToken: {},
    viewsToken: {}
  };

  const { data: news = [], isLoading: isNewsLoading } = useQueryFetch<News[]>(['news', pathname], async () => {
    const result: News[] = await getNews(false, tokens.newsToken);
    const userId = user.id ?? user._id ?? '';
    const { userRole } = user;

    return result.filter(n => {
      const roles = n.userRoles ?? [];
      const users = n.users ?? [];

      if (roles.length || users.length) {
        return roles.includes(userRole) || users.includes(userId);
      }

      return false;
    });
  });

  const currentNews = news?.[0];

  const { data: newsViews = [] } = useQueryFetch(
    ['news_views', currentNews?._id ?? ''],
    async () => {
      const result: NewsViews[] = await getNewsViewsById(currentNews!._id, tokens.newsViewsToken);
      const completedPages = new Set(result.filter(res => res.completed).map(res => res.page));
      const allPagesViewed = currentNews.pages.every(newsPage => completedPages.has(newsPage.page));

      let finalResult = result;

      if (!allPagesViewed && currentNews) {
        finalResult = await postNewsViews(currentNews._id, tokens.viewsToken);
      }

      const resultFiltered = finalResult.filter(item => !completedPages.has(item.page));
      const isAutoShow = !!currentNews?.autoShow;
      const validRoute = VALID_ROUTES.includes(pathname);

      setState(prevState => ({
        ...prevState,
        showModal: isAutoShow && !allPagesViewed && validRoute
      }));

      return resultFiltered;
    },
    {
      enabled: !isNewsLoading && news?.[0]?._id !== undefined
    }
  );

  useEffect(() => {
    return () => {
      tokens.newsToken.cancel?.();
    };
  }, []);

  const handleToggleIsActive = () => {
    setState((prev: State) => ({
      ...prev,
      isActive: !prev.isActive
    }));
  };

  const handleLastNewClick = async () => {
    setState((prev: State) => ({
      ...prev,
      showModal: true
    }));
  };

  const handleSectionClick = (section: 'management' | 'alerts') => {
    if (section === 'management') {
      const { sonataManagementAppV2Url } = taptap.config;
      window.open(sonataManagementAppV2Url, '_blank', 'noopener,noreferrer');
    } else {
      showNotificationSettingsModal('show');
    }
  };

  const viewsTokens = new Map(newsViews.map(view => [view.page, {}]));

  const handleNewsPageClick = async (page: number) => {
    const newsView = newsViews?.find(view => view.page === page);
    if (newsView && !newsView.completed) {
      const updatedNewsView = await updateNewsViews(newsView._id, true, page, viewsTokens.get(page) || {});
      queryClient.setQueryData<NewsViews[]>(['news_views', currentNews?._id], (currentNewsViews = []) => {
        return currentNewsViews.map(currentNewView =>
          currentNewView._id === newsView._id ? updatedNewsView : currentNewView
        );
      });
    }
  };

  const handleCloseModal = async () => {
    const newsViewsToMarkCompleted = newsViews.filter(v => !v.completed);
    if (currentNews?.skippeable && newsViewsToMarkCompleted?.[0]) {
      const updatedNewsViews = await Promise.all(
        newsViewsToMarkCompleted.map(v => updateNewsViews(v._id, true, v.page, viewsTokens.get(v.page) || {}))
      );
      queryClient.setQueryData<NewsViews[]>(['news_views', currentNews?._id], updatedNewsViews);
    }
    setState((prevState: State) => ({
      ...prevState,
      showModal: false
    }));
  };

  const isBadgeRequired = (): boolean => (newsViews.length > 0 ? newsViews.some(view => !view.completed) : false);

  const hasManagementNewsAccess = checkUserRouteAccess('newManagement', user, userRoles);
  const hasAlertNotificationsAccess = checkUserResourcePermissions('notifications', user, userRoles);

  return (
    <CustomThemeProvider themeContrast={theme}>
      <div className="newsButtonMenu" ref={ref}>
        <ButtonUI
          badge={isBadgeRequired()}
          className={cn('newsButtonMenu__button', { 'newsButtonMenu__button--active': isActive })}
          disabled={!currentNews && !newsViews?.[0]}
          icon="fa-regular fa-bell"
          id={'newButtonMenu_Button'}
          onClick={handleToggleIsActive}
          shape="link"
          size="medium"
        />
        {isActive && (
          <div className="newsButtonMenu__container">
            {!!currentNews && (
              <>
                <button className="newsButtonMenu__item" onClick={handleLastNewClick} type="button">
                  <i className="fa-regular fa-paintbrush-pencil newsButtonMenu__itemIcon" />
                  <div className="newsButtonMenu__itemText">
                    <div className="newsButtonMenu__itemTextTitle" title={currentNews?.menuTitle}>
                      {currentNews?.menuTitle}
                    </div>
                    <div className="newsButtonMenu__itemTextSubtitle" title={currentNews?.menuDescription}>
                      {currentNews?.menuDescription}
                    </div>
                  </div>
                </button>
                <div className="newsButtonMenu__divider" />
              </>
            )}
            {hasManagementNewsAccess && (
              <button className="newsButtonMenu__item" onClick={() => handleSectionClick('management')} type="button">
                <i className="fa-regular fa-gear newsButtonMenu__itemIcon" />
                <div className="newsButtonMenu__itemText">
                  <div className="newsButtonMenu__itemTextTitle">
                    {formatMessage({
                      defaultMessage: 'News management',
                      id: 'menu.notifications.newManagement'
                    })}
                  </div>
                </div>
              </button>
            )}
            {hasAlertNotificationsAccess && (
              <button className="newsButtonMenu__item" onClick={() => handleSectionClick('alerts')} type="button">
                <i className="fa-duotone fa-solid fa-siren-on newsButtonMenu__itemIcon" />
                <div className="newsButtonMenu__itemText">
                  <div className="newsButtonMenu__itemTextTitle">
                    {formatMessage({
                      defaultMessage: 'Alerts & Notifications',
                      id: 'menu.notifications.alerts'
                    })}
                  </div>
                </div>
              </button>
            )}
          </div>
        )}
        {showModal ? (
          <CustomThemeProvider themeContrast={theme}>
            <ModalComponent
              circleCloseButton
              hideCloseButton={!currentNews?.skippeable}
              isModalOpened={showModal}
              onClose={handleCloseModal}
              plainModal
              portalId="newsFeatures"
              theme="light"
              title=""
              width="805px"
            >
              <ModalComponent.ModalContent>
                <NewsFeatures
                  content={(currentNews?.pages || []).map(p => {
                    return {
                      ...p,
                      id: p.page.toString()
                    };
                  })}
                  onChange={handleNewsPageClick}
                  onClose={handleCloseModal}
                />
              </ModalComponent.ModalContent>
            </ModalComponent>
          </CustomThemeProvider>
        ) : null}
      </div>
    </CustomThemeProvider>
  );
}
